import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { updateInvestments } from "../../utilities/api";
import { normalizeDateKey } from "../home/shared";
import { TransactionFormDialog } from "./TransactionForm";

const DASHBOARD_KEYS = [
	"fetchInvestments",
	"fetchInvestmentSummary",
	"fetchAssets",
	"fetchAllAssetLatestPrice",
	"fetchDaily",
];

const TransactionEditModal = ({ open, transaction, onClose }) => {
	const queryClient = useQueryClient();
	const [error, setError] = useState("");

	useEffect(() => {
		if (open) setError("");
	}, [open, transaction]);

	const initialValues = useMemo(
		() =>
			transaction
				? {
						asset: transaction.asset || "",
						transactionType: transaction.transactionType || "BUY",
						transactionDate: normalizeDateKey(transaction.effectiveDate) || "",
						units: String(transaction.units ?? ""),
						// sourceValue/sourceCurrency, not the USD-normalized `value` -- editing a
						// CHF buy has to show the price that was actually entered.
						value: String(transaction.sourceValue ?? transaction.value ?? ""),
						currency: transaction.sourceCurrency || "USD",
					}
				: null,
		[transaction]
	);

	const mutation = useMutation(updateInvestments, {
		onSuccess: () => {
			DASHBOARD_KEYS.forEach((key) => queryClient.invalidateQueries(key));
			onClose();
		},
		onError: (err) => {
			const responseError = err?.response?.data?.error;
			const availableUnits = err?.response?.data?.availableUnits;
			if (responseError && typeof availableUnits === "number") {
				setError(`${responseError}. Available units: ${availableUnits}`);
				return;
			}
			setError(responseError || err.message || "Failed to update transaction");
		},
	});

	if (!open || !transaction) return null;

	return (
		<TransactionFormDialog
			open={open}
			onClose={onClose}
			title="Edit transaction"
			description="Amounts stay in the currency they were entered in, converted at the rate captured with the transaction."
			initialValues={initialValues}
			excludeId={transaction.id}
			submitLabel="Save changes"
			isSubmitting={mutation.isLoading}
			serverError={error}
			onSubmit={(values) => {
				setError("");
				mutation.mutate({
					id: transaction.id,
					asset: values.asset,
					value: values.value,
					units: values.units,
					currency: values.currency,
					transactionType: values.transactionType,
					transactionDate: values.transactionDate,
				});
			}}
		/>
	);
};

export default TransactionEditModal;
